/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { DirectAccessUrlEntry, RenditionEntry, RenditionPaging } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
export class RenditionsApi extends BaseApi {
    createRendition(nodeId, renditionBodyCreate) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(renditionBodyCreate, 'renditionBodyCreate');
        const pathParams = {
            nodeId
        };
        return this.post({
            path: '/nodes/{nodeId}/renditions',
            pathParams,
            bodyParam: renditionBodyCreate
        });
    }
    getRendition(nodeId, renditionId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(renditionId, 'renditionId');
        const pathParams = {
            nodeId,
            renditionId
        };
        return this.get({
            path: '/nodes/{nodeId}/renditions/{renditionId}',
            pathParams,
            returnType: RenditionEntry
        });
    }
    getRenditionContent(nodeId, renditionId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(renditionId, 'renditionId');
        const pathParams = {
            nodeId,
            renditionId
        };
        const queryParams = {
            attachment: opts === null || opts === void 0 ? void 0 : opts.attachment,
            placeholder: opts === null || opts === void 0 ? void 0 : opts.placeholder
        };
        const headerParams = {
            'If-Modified-Since': opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince,
            Range: opts === null || opts === void 0 ? void 0 : opts.range
        };
        const accepts = ['application/octet-stream'];
        return this.get({
            path: '/nodes/{nodeId}/renditions/{renditionId}/content',
            pathParams,
            queryParams,
            headerParams,
            accepts,
            returnType: 'blob'
        });
    }
    listRenditions(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        return this.get({
            path: '/nodes/{nodeId}/renditions',
            pathParams,
            queryParams: opts,
            returnType: RenditionPaging
        });
    }
    requestDirectAccessUrl(nodeId, renditionId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(renditionId, 'renditionId');
        const pathParams = {
            nodeId,
            renditionId
        };
        return this.post({
            path: '/nodes/{nodeId}/renditions/{renditionId}/request-direct-access-url',
            pathParams,
            returnType: DirectAccessUrlEntry
        });
    }
}
